export class CaseTransform {
  // incoming data parameter names
  private static readonly ATTRIBUTES_NAME = 'attributes';
  private static readonly FIELDS_NAME = 'fields';
  private static readonly FEATURES_NAME = 'features';
  private static readonly LAYERS_NAME = 'layers';
  private static readonly TABLES_NAME = 'tables';
  private static readonly RESULTS_NAME = 'results';
  private static readonly RELATED_RECORD_GROUPS_NAME = 'relatedRecordGroups';
  private static readonly SUBTYPES_NAME = 'subtypes';
  private static readonly OBJECT_ID_FIELD_NAME = 'objectIdField';
  private static readonly OBJECT_ID_FIELD_NAME_NAME = 'objectIdFieldName';
  private static readonly GLOBAL_ID_FIELD_NAME = 'globalIdField';
  private static readonly GLOBAL_ID_FIELD_NAME_NAME = 'globalIdFieldName';

  // outgoing request query parameter names
  private static readonly ADDS_NAME = 'adds';
  private static readonly UPDATES_NAME = 'updates';

  static canRequestBeTransformed(request: __esri.RequestOptions): boolean {
    return (
      request &&
      request.query &&
      typeof request.query === 'object' &&
      (CaseTransform.ADDS_NAME in request.query ||
        CaseTransform.UPDATES_NAME in request.query)
    );
  }

  static canDataObjectBeTransformed(data: any): boolean {
    return (
      data &&
      typeof data === 'object' &&
      (CaseTransform.FIELDS_NAME in data ||
        CaseTransform.FEATURES_NAME in data ||
        CaseTransform.LAYERS_NAME in data ||
        CaseTransform.TABLES_NAME in data ||
        CaseTransform.RESULTS_NAME in data ||
        CaseTransform.RELATED_RECORD_GROUPS_NAME in data ||
        CaseTransform.SUBTYPES_NAME in data ||
        CaseTransform.OBJECT_ID_FIELD_NAME in data ||
        CaseTransform.OBJECT_ID_FIELD_NAME_NAME in data ||
        CaseTransform.GLOBAL_ID_FIELD_NAME in data ||
        CaseTransform.GLOBAL_ID_FIELD_NAME_NAME in data)
    );
  }

  static transformRequestQueryToLowerCase(
    request: __esri.RequestOptions
  ): void {
    if (!request || !request.query || typeof request.query !== 'object') {
      return;
    }

    const query = request.query;

    let propName = CaseTransform.ADDS_NAME;
    if (
      propName in query &&
      query[propName] &&
      typeof query[propName] === 'string'
    ) {
      query[propName] = this.transformRequestQueryCrudDataToLowerCase(
        query[propName]
      );
    }

    propName = CaseTransform.UPDATES_NAME;
    if (
      propName in query &&
      query[propName] &&
      typeof query[propName] === 'string'
    ) {
      query[propName] = this.transformRequestQueryCrudDataToLowerCase(
        query[propName]
      );
    }
  }

  static transformDataObjectToUpperCase(data: any): void {
    this.transformDataObject(data, true);
  }

  private static transformRequestQueryCrudDataToLowerCase(
    dataString: string
  ): string {
    let transformedDataString = dataString;
    try {
      // convert from JSON string to object
      const data = JSON.parse(dataString);

      // transform the object to lower case
      if (data && Array.isArray(data)) {
        data
          .filter(entry => entry && typeof entry === 'object')
          .forEach(entry => {
            this.transformDataObject(entry, false);
          });
      }

      // re-convert from object to JSON string
      transformedDataString = JSON.stringify(data);
    } catch (error) {
      console.error(`Cannot convert Esri JSON string to object: ${error}`);
    }

    return transformedDataString;
  }

  private static transformDataObject(
    data: any,
    transformToUpperCase: boolean
  ): void {
    if (!data || typeof data !== 'object') {
      return;
    }

    let propName = CaseTransform.FIELDS_NAME;
    if (propName in data && data[propName] && Array.isArray(data[propName])) {
      data[propName]
        .filter(field => field['name'] && typeof field['name'] === 'string')
        .forEach(field => {
          field['name'] = transformToUpperCase
            ? field['name'].toUpperCase()
            : field['name'].toLowerCase();
        });
    }

    propName = CaseTransform.FEATURES_NAME;
    if (propName in data && data[propName] && Array.isArray(data[propName])) {
      data[propName].forEach(feature => {
        this.transformDataObject(feature, transformToUpperCase);
      });
    }

    propName = CaseTransform.SUBTYPES_NAME;
    if (propName in data && data[propName] && Array.isArray(data[propName])) {
      data[propName]
        .filter(subtype => typeof subtype === 'object')
        .forEach(subtype => {
          let subProbName = 'domains';
          if (
            subProbName in subtype &&
            subtype[subProbName] &&
            typeof subtype[subProbName] === 'object'
          ) {
            this.transformObjectKeys(
              subtype[subProbName],
              transformToUpperCase
            );
          }
          subProbName = 'defaultValues';
          if (
            subProbName in subtype &&
            subtype[subProbName] &&
            typeof subtype[subProbName] === 'object'
          ) {
            this.transformObjectKeys(
              subtype[subProbName],
              transformToUpperCase
            );
          }
        });
    }

    propName = CaseTransform.OBJECT_ID_FIELD_NAME;
    if (
      propName in data &&
      data[propName] &&
      typeof data[propName] === 'string'
    ) {
      data[propName] = transformToUpperCase
        ? data[propName].toUpperCase()
        : data[propName].toLowerCase();
    }

    propName = CaseTransform.OBJECT_ID_FIELD_NAME_NAME;
    if (
      propName in data &&
      data[propName] &&
      typeof data[propName] === 'string'
    ) {
      data[propName] = transformToUpperCase
        ? data[propName].toUpperCase()
        : data[propName].toLowerCase();
    }

    propName = CaseTransform.GLOBAL_ID_FIELD_NAME;
    if (
      propName in data &&
      data[propName] &&
      typeof data[propName] === 'string'
    ) {
      data[propName] = transformToUpperCase
        ? data[propName].toUpperCase()
        : data[propName].toLowerCase();
    }

    propName = CaseTransform.GLOBAL_ID_FIELD_NAME_NAME;
    if (
      propName in data &&
      data[propName] &&
      typeof data[propName] === 'string'
    ) {
      data[propName] = transformToUpperCase
        ? data[propName].toUpperCase()
        : data[propName].toLowerCase();
    }

    propName = CaseTransform.LAYERS_NAME;
    if (propName in data && data[propName] && Array.isArray(data[propName])) {
      data[propName]
        .filter(layer => layer && typeof layer === 'object')
        .forEach(layer => {
          this.transformDataObject(layer, transformToUpperCase);
        });
    }

    propName = CaseTransform.TABLES_NAME;
    if (propName in data && data[propName] && Array.isArray(data[propName])) {
      data[propName].forEach(table => {
        this.transformDataObject(table, transformToUpperCase);
      });
    }

    propName = CaseTransform.RESULTS_NAME;
    if (propName in data && data[propName] && Array.isArray(data[propName])) {
      data[propName].forEach(result => {
        this.transformDataObject(result, transformToUpperCase);
      });
    }

    // related & relation tables
    propName = CaseTransform.RELATED_RECORD_GROUPS_NAME;
    if (propName in data && data[propName] && Array.isArray(data[propName])) {
      data[propName]
        .filter(relatedRecordGroup => typeof relatedRecordGroup === 'object')
        .forEach(relatedRecordGroup => {
          relatedRecordGroup.relatedRecords.forEach(relatedRecord => {
            this.transformDataObject(relatedRecord, transformToUpperCase);
          });
        });
    }

    propName = CaseTransform.ATTRIBUTES_NAME;
    if (
      propName in data &&
      data[propName] &&
      typeof data[propName] === 'object'
    ) {
      this.transformObjectKeys(data[propName], transformToUpperCase);
    }
  }

  private static transformObjectKeys(
    object: Object,
    transformToUpperCase: boolean
  ): void {
    if (!object) {
      return;
    }
    Object.keys(object)
      .filter(
        key =>
          key !== (transformToUpperCase ? key.toUpperCase() : key.toLowerCase())
      )
      .forEach(key => {
        // assigns a new key in upper/lower case and removes the old one
        delete Object.assign(object, {
          [transformToUpperCase ? key.toUpperCase() : key.toLowerCase()]:
            object[key],
        })[key];
      });
  }
}
